import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useTheme } from 'styled-components';
import { useTranslate } from '@/translations';
import {
  Title,
  faShip,
  PageContainer,
  Map,
  GRIMSBY_MAP_CENTER,
} from '@/ui/components';
import { useVesselMovements, useVesselPositions } from '@/api/Vessel';
import { Button, Card, Col, Row, Spin } from 'antd';
import { VesselFilters } from './components/VesselFilters';
import { VesselDataGrid } from './components/VesselDataGrid';
import {
  formatVesselMovements,
  getVesselMovementsFilters,
  filterVesselMovements,
  getFilteredVesselPositions,
} from './utils';
import { VesselFiltersValues } from './types';
import { UploadVesselModal } from './components/UploadVesselModal';

export const Vessel = () => {
  const [filterValues, setFilterValues] = useState<VesselFiltersValues>({});
  const [selectedMmsi, setSelectedMmsi] = useState<number[] | null>(null);
  const { t } = useTranslate();
  const { vesselsLink } = useTheme();
  const { data, isFetching } = useVesselMovements();
  const { data: vesselPositionsData } = useVesselPositions();
  const formattedVesselMovements = useMemo(
    () => formatVesselMovements(data?.entities || []),
    [data?.entities],
  );

  const vesselMovementsFiltersData = useMemo(
    () => getVesselMovementsFilters(formattedVesselMovements),
    [formattedVesselMovements],
  );

  const filteredVesselMovements = useMemo(
    () => filterVesselMovements(formattedVesselMovements, filterValues),
    [formattedVesselMovements, filterValues],
  );

  const [showUploadDocument, setShowUploadDocument] = useState(false);

  useEffect(() => {
    setSelectedMmsi(filteredVesselMovements.map((mov) => mov.mmsi));
  }, [filteredVesselMovements]);

  const filteredVesselPositions = useMemo(
    () =>
      getFilteredVesselPositions(
        vesselPositionsData?.entities || [],
        selectedMmsi,
        filteredVesselMovements,
      ),
    [vesselPositionsData?.entities, selectedMmsi, filteredVesselMovements],
  );

  const handleFiltersChange = (filters: VesselFiltersValues) => {
    setFilterValues(filters);
  };

  const handleSelectVesselGroup = useCallback((mmsi: number | null) => {
    setSelectedMmsi(mmsi ? [mmsi] : null);
  }, []);

  return (
    <Spin spinning={isFetching}>
      <PageContainer>
        <Title title={t('vesselTitle')} icon={faShip} color={vesselsLink} />

        <VesselFilters
          initData={vesselMovementsFiltersData}
          onChange={handleFiltersChange}
        />
        {/* <Icon icon={faUpload} onClick={handleUpload} /> */}
        <Row gutter={16}>
          <Col lg={12} sm={24}>
            <Card>
              <Button
                type="primary"
                size="large"
                onClick={() => setShowUploadDocument(true)}
              >
                {t('uploadDocument')}
              </Button>
              <VesselDataGrid
                dataSource={filteredVesselMovements}
                onSelectVesselGroup={handleSelectVesselGroup}
              />
            </Card>
          </Col>
          <Col lg={12} sm={24}>
            <Card className="h-100" styles={{ body: { height: '100%' } }}>
              <Map
                markers={filteredVesselPositions.map(({ lat, lon }) => ({
                  lat,
                  lng: lon,
                }))}
                icon="/images/GBA BOAT icons8-boat-64 v3.png"
                zoom={5}
                center={GRIMSBY_MAP_CENTER}
                vessels={filteredVesselPositions.map(
                  ({
                    mmsi,
                    status,
                    speed,
                    lon,
                    lat,
                    course,
                    heading,
                    timestamp,
                    shipName,
                  }) => ({
                    mmsi,
                    status,
                    speed,
                    lon,
                    lat,
                    course,
                    heading,
                    timestamp,
                    shipName,
                  }),
                )}
              />
            </Card>
          </Col>
        </Row>
        <UploadVesselModal
          isOpen={showUploadDocument}
          closeModal={() => setShowUploadDocument(false)}
        />
      </PageContainer>
    </Spin>
  );
};
